import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

/** The models selectable for AI Color Tagging (only priced ones) and the default used when none is set. */
export interface AiColorTaggerModelOptions {
  models: string[];
  defaultModel: string;
}

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  private readonly apiURLprefix : string = '/api/cms-settings';
  constructor(private http: HttpClient) {}

  /**
   * Updates CMS Setting record by Id
   * 
   * @param settingId Id of the cms settings
   * @param settingsData Request dto with data to update cms setting record
   */
  async updateSetting(settingId: any, settingsData: any): Promise<any> {
    return firstValueFrom(this.http.patch(`${this.apiURLprefix}/update/${settingId}`, settingsData));
  }

  /**
   * Retrieves a CMS Setting by a given query
   * 
   * @param query Query instruction
   */
  async getSettingByQuery(query: Object) {
    return firstValueFrom(this.http.post<any>(`${this.apiURLprefix}/find`, query));
  }

  /**
   * Retrieves the models selectable for AI Color Tagging (only models with a known price) and the
   * default used when none is chosen.
   */
  async getAiColorTaggerModels(): Promise<AiColorTaggerModelOptions> {
    return firstValueFrom(this.http.get<AiColorTaggerModelOptions>(`${this.apiURLprefix}/ai-color-tagger-models`));
  }

  /**
   * Retrieves a CMS Setting by a given query
   * 
   * @param query Query instruction
   */
  async setSettingByQuery(dto: Object) {
    return firstValueFrom(this.http.post<any>(`${this.apiURLprefix}/add`, dto));
  }

  /**
   * Retrieves a CMS Setting by a given query
   * 
   * @param query Query instruction
   */
  async getRemoteCollections() {
    return firstValueFrom(this.http.get<any>(`/api/worker/builder/remote/collections`));
  }

  /**
   * Updates CMS Setting record by Id with conflict detection
   *
   * @param settingId Id of the cms settings
   * @param settingsData Request dto with data to update cms setting record
   * @param fetchedAt Timestamp when the record was originally fetched (for conflict detection)
   */
  async updateSettingV2(settingId: any, settingsData: any, fetchedAt: Date): Promise<any> {
    return firstValueFrom(
      this.http.patch(`${this.apiURLprefix}/update/v2/${settingId}`, {
        dto: settingsData,
        fetchedAt,
        checkConflicts: true,
      }),
    );
  }

  /**
   * Syncs collections to local
   *
   * @param collections Collections to sync
   */
  async syncToLocal(collections: string[]) {
    return firstValueFrom(this.http.post<any>(`/api/worker/builder/sync/local`, { collections }));
  }
}
